<template>
    <div class="relative flex flex-row justify-between items-center gap-1 text-white dark:!text-white text-xs w-[120px]">
        <!-- Timer Display -->
        <div class="text-center">
            <div v-if="isPlaying" class="flex items-center justify-center gap-1">
                <span class="text-xs">Playing</span>
                <span class="animate-pulse text-green-400 text-xs">●</span>
            </div>
            <div v-else class="flex items-center justify-center gap-1">
                <span class="text-xs">Stopped</span>
                <span class="text-red-400 text-xs">●</span>
            </div>
            <div class="text-xs font-mono font-normal mt-1" :class="mode === 'countdown' && currentTime <= 0 ? 'text-red-400' : 'text-white'">
                {{timerValue}}
            </div>
        </div>

        <!-- Configuration Button -->
        <button v-if="!isPlaying && !props.disabled"
            @click="timerConfiguration.showConfig = !timerConfiguration.showConfig"
            class="p-1 text-gray-400 hover:text-white dark:text-white hover:bg-gray-700 dark:hover:bg-black/10 rounded transition-colors float-menu-button"
            title="Timer Configuration"
        >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
            </svg>
        </button>

        <!-- Configuration Popup -->
        <TimerConfiguration ref="timerConfiguration" 
            @setMode="setMode" 
            @setPreset="setPreset" 
            @resetTimer="resetTimer" />

        <!-- Backdrop to close popup -->
        <div 
            v-if="showConfig" 
            @click="showConfig = false"
            class="fixed inset-0 z-40 bg-black bg-opacity-25"
        ></div>
    </div>
</template>
<script setup>
import { ref, computed, watch, defineExpose, defineEmits, defineProps, onMounted, nextTick } from 'vue';
import TimerConfiguration from '@/components/common/TimerConfiguration.vue';

const props = defineProps({
    disabled: {
        type: Boolean,
        default: false
    },
    startAutomatically: {
        type: Boolean,
        default: false
    }
});
// Timer state
const timerValue = ref('00:00:00');
const timerInterval = ref(null);
const isPlaying = ref(false);
const mode = ref('countup'); // 'countdown' or 'countup'
const currentTime = ref(0); // Current time in seconds
const showConfig = ref(false); // Popup visibility
const timerConfiguration = ref(null);

const emits = defineEmits(['reset']);

// Input fields for countdown mode
const inputHours = ref(0);
const inputMinutes = ref(0);
const inputSeconds = ref(0);

// Computed property for formatted time
const formattedTime = computed(() => {
    const hours = Math.floor(Math.abs(currentTime.value) / 3600);
    const minutes = Math.floor((Math.abs(currentTime.value) % 3600) / 60);
    const seconds = Math.abs(currentTime.value) % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
});

// Watch for changes in currentTime to update display
watch(currentTime, () => {
    timerValue.value = formattedTime.value;
});

// Watch for input changes to update currentTime in countdown mode
watch([inputHours, inputMinutes, inputSeconds], () => {
    if (mode.value === 'countdown') {
        currentTime.value = (inputHours.value * 3600) + (inputMinutes.value * 60) + inputSeconds.value;
    }
});

const setMode = (newMode) => {
    if (isPlaying.value) {
        stopTimer();
    }
    mode.value = newMode;
    
    if (newMode === 'countdown') {
        // Initialize with input values
        currentTime.value = (inputHours.value * 3600) + (inputMinutes.value * 60) + inputSeconds.value;
    } else {
        // Reset to zero for count-up mode
        currentTime.value = 0;
    }
};

const startTimer = () => {
    if (mode.value === 'countdown' && currentTime.value <= 0) {
        return; // Don't start countdown if time is already zero or negative
    }
    
    timerInterval.value = setInterval(() => {
        if (mode.value === 'countdown') {
            currentTime.value--;
            if (currentTime.value <= 0) {
                currentTime.value = 0;
                stopTimer();
            }
        } else {
            currentTime.value++;
        }
    }, 1000);
    isPlaying.value = true;
    showConfig.value = false;
};

const stopTimer = () => {
    clearInterval(timerInterval.value);
    timerInterval.value = null;
    isPlaying.value = false;
    showConfig.value = false;
};

const resetTimer = () => {
    stopTimer();
    if (mode.value === 'countdown') {
        currentTime.value = (inputHours.value * 3600) + (inputMinutes.value * 60) + inputSeconds.value;
    } else {
        currentTime.value = 0;
    }
    emits('reset');
};

const toggleTimer = () => {
    if (isPlaying.value) {
        stopTimer();
    } else {
        startTimer();
    }
};

const setPreset = (preset) => {
    inputHours.value = preset.hours;
    inputMinutes.value = preset.minutes;
    inputSeconds.value = preset.seconds;
    // Update current time immediately
    currentTime.value = (preset.hours * 3600) + (preset.minutes * 60) + preset.seconds;
};

// Initialize timer value
timerValue.value = formattedTime.value;

onMounted(() => {
    nextTick(() => {
        if (props.startAutomatically) {
            startTimer();
        }
    });
});

defineExpose({
    toggleTimer,
    startTimer,
    stopTimer,
    resetTimer,
    setMode,
    setPreset,
    mode,
    currentTime,
    showConfig
});
</script>